import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { notifyWarning } from '@/services/notificationService'
import { languageService } from '@/services/languageService'
import { useMaintenanceStore, formatMaintenanceEnd } from '@/stores/maintenanceStore'

const MAINTENANCE_NOTIFY_INTERVAL_MS = 60 * 1000
const MAINTENANCE_STATUSES = [500, 502, 503, 504]

let lastMaintenanceNotification: number | null = null

export function resetMaintenanceNotificationThrottle() {
    lastMaintenanceNotification = null
}

function isMaintenanceResponse(error: AxiosError): boolean {
    const data = error?.response?.data as { code?: string } | undefined
    return error?.response?.status === 503 && data?.code === 'maintenance'
}

function isMaintenanceError(error: AxiosError, maintenanceActive: boolean): boolean {
    if (isMaintenanceResponse(error)) {
        return true
    }

    if (!maintenanceActive || axios.isCancel(error)) {
        return false
    }

    const status = error?.response?.status
    return status === undefined || MAINTENANCE_STATUSES.includes(status)
}

export function notifyMaintenance(error: AxiosError): boolean {
    const maintenanceStore = useMaintenanceStore()

    if (!isMaintenanceError(error, maintenanceStore.isActive)) {
        return false
    }

    if (!maintenanceStore.isActive) {
        maintenanceStore.refresh()
    }

    const now = Date.now()
    if (lastMaintenanceNotification !== null && now - lastMaintenanceNotification < MAINTENANCE_NOTIFY_INTERVAL_MS) {
        return true
    }
    lastMaintenanceNotification = now

    const t = languageService.getI18n().global.t
    const notice = maintenanceStore.notice

    const message = maintenanceStore.isActive && notice?.endUtc
        ? t('maintenance.errorMessage', { end: formatMaintenanceEnd(notice.startUtc, notice.endUtc, languageService.getLocale()) })
        : t('maintenance.errorMessageNoEnd')

    notifyWarning(t('maintenance.errorTitle'), message)
    return true
}

export function handleError(error: AxiosError) {
    if (notifyMaintenance(error)) {
        return null
    }
    if (error?.response?.status === 500) {
        notifyWarning("API error", "Try again later, or contact LoopWord")
    }
    if (error?.response?.status === 400) {
        notifyWarning("Something went wrong", "Please check configuration or your file")
    }
    return null
}

export function authorisationHeader(accessToken: string): AxiosRequestConfig {
    return {
        headers: {
            Authorization: accessToken
        }
    };
}

export async function apiGet(url: string, params = {}) {
    try {
        const response = await axios.get(url, params)
        return response.status === 200 ? response.data : null
    } catch (error) {
        return handleError(error as AxiosError)
    }
}

export async function apiPost(url: string, data = {}, unsafeCall = false, config: AxiosRequestConfig = {}) {
    try {
        const response = await axios.post(url, data, config)
        return response.status === 200 ? response.data : null
    } catch (error) {
        if (unsafeCall) throw error
        return handleError(error as AxiosError)
    }
}
